import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABEL = { instructor: "Instructor", admin: "Kheru · Founder", ambassador: "Ambassador" };

const Thread = ({ other }) => {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const end = useRef(null);
  const load = () => api.get(`/messages/${other.id}`).then((r) => setMsgs(r.data)).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [other.id]);
  useEffect(() => {
    const box = end.current?.parentElement;
    if (box) box.scrollTop = box.scrollHeight;
  }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/messages/${other.id}`, { body: text.trim() });
      setText("");
      load();
    } catch (err) {
      toast.error(formatErr(err) || "Your message could not be sent. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-[32rem]" data-testid="message-thread">
      <div className="px-6 py-4 border-b border-[#dacac1]">
        <p className="font-serif-h text-2xl">{other.name}</p>
        <p className="text-xs text-[#b6689a]">{ROLE_LABEL[other.role]}</p>
      </div>
      <div className="flex-1 overflow-y-auto p-6 space-y-3">
        {msgs.length === 0 && <p className="text-sm text-[#232b1b]/55">Begin with a gentle hello.</p>}
        {msgs.map((m) => (
          <div key={m.id} className={`max-w-[75%] rounded-2xl px-4 py-3 text-sm ${m.from_id === user.id ? "ml-auto bg-[#577731] text-white" : "bg-[#dacac1]/45"}`} data-testid="message-bubble">
            {m.body}
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="p-4 border-t border-[#dacac1] flex gap-2">
        <input className="field" placeholder="Write with care…" value={text} onChange={(e) => setText(e.target.value)} data-testid="message-input" />
        <button disabled={sending} className="btn btn-olive !py-2" data-testid="message-send-btn">{sending ? "Sending…" : "Send"}</button>
      </form>
    </div>
  );
};

export const Messages = () => {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [contacts, setContacts] = useState(null);
  const [active, setActive] = useState(null);
  const threadRef = useRef(null);
  useEffect(() => {
    api.get("/messages/contacts").then((r) => {
      setContacts(r.data);
      const to = r.data.find((c) => c.id === params.get("to"));
      if (to) setActive(to);
    }).catch(() => setContacts([]));
  }, [params]);
  const choose = (c) => {
    setActive(c);
    if (window.innerWidth >= 768) return;
    setTimeout(() => {
      const el = threadRef.current;
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 124, behavior: "smooth" });
    }, 50);
  };
  return (
    <div data-testid="members-messages">
      <span className="eyebrow">Private Messaging</span>
      <h1 className="font-serif-h font-light text-4xl sm:text-5xl text-[#577731] mt-4">Direct connection to your instructors and fellow Ambassadors</h1>
      <div className="card-soft mt-10 grid md:grid-cols-3 overflow-hidden">
        <ul className="border-b md:border-b-0 md:border-r border-[#dacac1] max-h-[32rem] overflow-y-auto">
          {contacts?.length === 0 && (
            <li className="px-6 py-5 text-sm text-[#232b1b]/60" data-testid="contacts-empty">No one else is in the circle yet.{user.role === "admin" ? " People appear here once you approve them." : ""}</li>
          )}
          {contacts?.map((c) => (
            <li key={c.id}>
              <button onClick={() => choose(c)} data-testid={`contact-${c.id}`}
                className={`w-full text-left px-6 py-4 transition-colors duration-300 ${active?.id === c.id ? "bg-[#b1cc90]/35" : "hover:bg-[#dacac1]/25"}`}>
                <span className="block text-sm font-medium">{c.name}</span>
                <span className="block text-xs text-[#b6689a]">{ROLE_LABEL[c.role]}</span>
              </button>
            </li>
          ))}
        </ul>
        <div className="md:col-span-2 scroll-mt-28" ref={threadRef}>
          {active ? <Thread other={active} /> : <p className="p-10 text-[#232b1b]/60">Choose someone to speak with.</p>}
        </div>
      </div>
      {user.role === "admin" && (
        <p className="mt-4 text-xs text-[#232b1b]/55" data-testid="messages-admin-note">Members waiting for approval are not listed here until you approve them in Admin → Members &amp; Enrollment.</p>
      )}
    </div>
  );
};
